import { Grid, useMediaQuery } from "@mui/material";
import { Create, Delete } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { ListAction, ListActions, ListItem, StyledPagination } from "@framework/styled";
import type { IContract } from "@framework/types";
import { ContractStatus, Erc721ContractTemplates } from "@framework/types";
import { useDialogDelete, useDialogForm, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import {
  BlacklistButton,
  ContractAllowanceButton,
  ContractMintButton,
  ContractSearchForm,
  Erc721ContractDeployButton,
  GrantRoleButton,
  RenounceRoleButton,
  RevokeRoleButton,
  RoyaltyButton,
  ServiceType,
  TransferButton,
  UnBlacklistButton,
  UnWhitelistButton,
  WhitelistButton,
  WithCheckPermissionsListWrapper,
} from "@framework/shared";
import { List } from "@dzambalaorg/ui-kit";

import { LotteryDialogForm } from "./form";

const LOTTERY_TICKETS_CONTRACT_DIALOG_KEY = "lotteryTicketsContract";
const LOTTERY_TICKETS_CONTRACTS_URL = "/lottery/ticket/contracts";

export const LotteryTicketContracts = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { data, isLoading, isError, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } =
    useFetchQuery<IPaginationResult<IContract>>(LOTTERY_TICKETS_CONTRACTS_URL, { enableInfiniteScroll: isMd }, void 0, {
      contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
    });

  const { handleForm, mode, selectedItem, formDialogUniqueKey } = useDialogForm<IContract>();
  const { handleDelete } = useDialogDelete<IContract>();

  const { address: account = "" } = useAccount();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "lottery", "lottery.tickets"]} />

      <PageHeader message="pages.lottery.tickets.title">
        <Erc721ContractDeployButton contractTemplate={Erc721ContractTemplates.LOTTERY} />
      </PageHeader>

      <ContractSearchForm
        initialSearchValues={{
          contractStatus: [ContractStatus.ACTIVE, ContractStatus.NEW],
        }}
        service={ServiceType.ADMIN}
        contractFeaturesOptions={{}}
      />

      <WithCheckPermissionsListWrapper
        isLoading={!data && isLoading}
        isError={!data && isError}
        isEmpty={allRows.length === 0}
        count={allRows.length}
      >
        {allRows.map(contract => (
          <ListItem key={contract.id} account={account} contract={contract}>
            <List.ItemText sx={{ width: 0.6 }}>{contract.title}</List.ItemText>
            <ListActions dataTestId="ContractActionsMenuButton">
              <ListAction
                onClick={handleForm(LOTTERY_TICKETS_CONTRACT_DIALOG_KEY, contract, "edit")}
                message="form.buttons.edit"
                dataTestId="ContractEditButton"
                icon={Create}
              />
              <ListAction
                onClick={handleDelete(contract)}
                message="form.buttons.delete"
                dataTestId="ContractDeleteButton"
                icon={Delete}
                disabled={contract.contractStatus === ContractStatus.INACTIVE}
              />
              <GrantRoleButton contract={contract} />
              <RevokeRoleButton contract={contract} />
              <RenounceRoleButton contract={contract} />
              <BlacklistButton contract={contract} />
              <UnBlacklistButton contract={contract} />
              <WhitelistButton contract={contract} />
              <UnWhitelistButton contract={contract} />
              <ContractMintButton contract={contract} />
              <ContractAllowanceButton contract={contract} />
              <TransferButton contract={contract} />
              <RoyaltyButton contract={contract} />
            </ListActions>
          </ListItem>
        ))}
      </WithCheckPermissionsListWrapper>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={LOTTERY_TICKETS_CONTRACTS_URL} refetch={refetch} />

      <LotteryDialogForm
        open={formDialogUniqueKey === `${LOTTERY_TICKETS_CONTRACT_DIALOG_KEY}.${mode}`}
        mode={mode}
        selectedItem={selectedItem}
        url={LOTTERY_TICKETS_CONTRACTS_URL}
        refetch={refetch}
      />
    </Grid>
  );
};
